import {
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useEffect,
  useReducer,
  useState,
} from 'react';
import { createContext, useContextSelector } from 'use-context-selector';
import { BagType } from '@/components/MeasurementAssistant/types';
import { UploadedFileType } from '@/hooks/defect-details/use-upload-additional-photos';
import { UploadImageType } from '@/services/qc.service';
import { AdditionalItemType } from '@/types/AdditionalItem';
import { CheckPointType } from '@/types/Checkpoint';
import {
  ActualMeasurement,
  SubmittedDefect,
} from '@/types/interfaces/defect-details';
import { ProductOriginCountry } from '@/types/ProductInfo';
import { AdditionalInfo, QcInfoType } from '@/types/QcInfo';

interface SetHasIssuePayloadType {
  checkPoint: CheckPointType;
  hasIssue: boolean;
}

type SetDefectType = SubmittedDefect & { checkPointId: number };

export interface SetDefectPayloadType {
  data: SetDefectType[];
}
interface DeleteDefectPayloadType {
  id: number;
}

interface DeleteDefectsPayloadType {
  checkpointId: number;
}

type QCPhotoType = {
  internal: Array<UploadedFileType>;
  public: Array<UploadedFileType>;
};

interface QualityControlState {
  additionalItems: AdditionalItemType[];
  agentComment: string;
  agentPublicComment: string;
  weight: string;
  hallmarks: string;
  originCountry: ProductOriginCountry;
  defects: SetDefectPayloadType['data'];
  qcPhotos: QCPhotoType;
  qcData?: QcInfoType;
  isUSShipping: boolean;
  isForceSellerReturnEditable: boolean;
  additionalInfo: AdditionalInfo;
  actualMeasurements: ActualMeasurement[];
  measurementAssistantBagType: BagType | null;
}
interface QualityControlContextType extends QualityControlState {
  setAdditionalItems: (additionalItems: AdditionalItemType[]) => void;
  setWeight: (weight: string) => void;
  setAgentComment: (agentComment: string) => void;
  setAgentPublicComment: (comment: string) => void;
  setHallmarks: (hallmarks: string) => void;
  setOriginCountry: (data: ProductOriginCountry) => void;
  setDefect: ({ data }: SetDefectPayloadType) => void;
  addDefect: ({ data }: SetDefectPayloadType) => void;
  deleteDefect: ({ id }: DeleteDefectPayloadType) => void;
  deleteDefects: ({ checkpointId }: DeleteDefectsPayloadType) => void;
  updateQCPhotos: (paths: UploadedFileType[], type: UploadImageType) => void;
  updateDefect: (data: SetDefectType) => void;
  setQcData: (data: QcInfoType) => void;
  setAdditionalInfo: Dispatch<SetStateAction<AdditionalInfo>>;
  setActualMeasurements: (measurements: ActualMeasurement[]) => void;
  setMeasurementAssistantBagType: (bagType: BagType | null) => void;
}

type ActionType =
  | {
      type: 'SET_HAS_ISSUE';
      payload: SetHasIssuePayloadType;
    }
  | {
      type: 'SET_DEFECT';
      payload: SetDefectPayloadType;
    }
  | {
      type: 'DELETE_DEFECT';
      payload: DeleteDefectPayloadType;
    }
  | {
      type: 'DELETE_DEFECTS';
      payload: DeleteDefectsPayloadType;
    }
  | {
      type: 'UPDATE_DEFECT';
      payload: SetDefectType;
    }
  | {
      type: 'ADD_DEFECT';
      payload: SetDefectPayloadType;
    };

export enum MODAL_TYPES {
  IMAGE_GALLERY = 'IMAGE_GALLERY',
  DEFECT_CREATION = 'DEFECT_CREATION',
  DEFECT_VIEW = 'DEFECT_VIEW',
  QC_SUBMIT = 'QC_SUBMIT',
  ADDITIONAL_ITEMS = 'ADDITIONAL_ITEMS',
  WARNING_INFO = 'WARNING_INFO',
  MEASUREMENT_ASSISTANT = 'MEASUREMENT_ASSISTANT',
}

const initialState: QualityControlState = {
  additionalItems: [],
  agentComment: '',
  agentPublicComment: '',
  weight: '',
  hallmarks: '',
  additionalInfo: {
    forceSellerReturn: false,
    refurbishment: false,
  },
  defects: [],
  qcPhotos: {
    internal: [],
    public: [],
  },
  originCountry: {
    legacyId: '',
    isConfirmed: false,
  },
  isUSShipping: false,
  isForceSellerReturnEditable: true,
  actualMeasurements: [],
  measurementAssistantBagType: null,
};

const qualityControlReducer = (
  state: QualityControlState,
  action: ActionType
): QualityControlState => {
  const { payload } = action;

  switch (action.type) {
    case 'ADD_DEFECT': {
      const { data } = payload as SetDefectPayloadType;

      // Merge the two arrays
      const mergedDefects = [...state.defects, ...data];

      // Remove duplicates based on the `id` property
      const uniqueDefects = mergedDefects.filter(
        (defect, index, self) =>
          index === self.findIndex((d) => d.id === defect.id)
      );

      return {
        ...state,
        defects: uniqueDefects,
      };
    }
    case 'SET_DEFECT': {
      const { data } = payload as SetDefectPayloadType;
      return {
        ...state,
        defects: [...data],
      };
    }
    case 'DELETE_DEFECT': {
      const { id } = payload as DeleteDefectPayloadType;

      const defects = state.defects?.length
        ? state.defects.filter((d) => d.id !== id)
        : state.defects;

      return {
        ...state,
        defects: [...defects],
      };
    }
    case 'DELETE_DEFECTS': {
      const { checkpointId } = payload as DeleteDefectsPayloadType;

      const defects = state.defects?.length
        ? state.defects.filter((d) => d.checkPointId !== checkpointId)
        : state.defects;

      return {
        ...state,
        defects: [...defects],
      };
    }
    case 'UPDATE_DEFECT': {
      const data = payload as SetDefectType;

      const index = state.defects.findIndex((d) => d.id === data.id);

      if (index < 0) return { ...state };

      const defects = [...state.defects];

      defects.splice(index, 1, data);

      return {
        ...state,
        defects,
      };
    }
    default:
      return state;
  }
};

interface QualityControlProviderProps {
  data?: QcInfoType;
  children: ReactNode;
}

export const QualityControlContext = createContext<QualityControlContextType>(
  initialState as QualityControlContextType
);

export const QualityControlProvider: FC<QualityControlProviderProps> = ({
  data,
  children,
}) => {
  const [qcData, setQcData] = useState<QcInfoType>();

  const [state, dispatch] = useReducer(qualityControlReducer, initialState);
  const [agentComment, setAgentComment] = useState<string>('');
  const [agentPublicComment, setAgentPublicComment] = useState<string>('');
  const [weight, setWeight] = useState<string>('');
  const [hallmarks, setHallmarks] = useState<string>('');
  const [additionalItems, setAdditionalItems] = useState<AdditionalItemType[]>(
    []
  );

  const [additionalInfo, setAdditionalInfo] = useState<AdditionalInfo>(
    initialState.additionalInfo
  );

  const [actualMeasurements, setActualMeasurements] = useState<
    ActualMeasurement[]
  >([]);

  const [measurementAssistantBagType, setMeasurementAssistantBagType] =
    useState<BagType | null>(null);

  const [originCountry, setOriginCountry] = useState<ProductOriginCountry>(
    initialState.originCountry
  );

  const [qcPhotos, setQCPhotos] = useState<QCPhotoType>({
    internal: [],
    public: [],
  });

  const { product } = data || {};

  // This is for Tax & Duties only applied in US
  const isUSShipping =
    !!product &&
    product.deliveryCountry?.isoCode?.toUpperCase() === 'US' &&
    product.warehouse?.isoCode?.toUpperCase() !== 'US';

  // Defaults to editable so the checkbox keeps working before the backend field ships
  const isForceSellerReturnEditable = data?.forceSellerReturnEditable ?? true;

  useEffect(() => {
    if (data) {
      setQcData(data);
      setOriginCountry((prev) => ({
        ...prev,
        isConfirmed:
          data.record?.cooConfirmation != null
            ? !!data.record.cooConfirmation
            : false,
        legacyId: data.record?.actualCooId || data.product?.cooId || '',
      }));
    }
  }, [data]);

  const setDefect = ({ data }: SetDefectPayloadType) => {
    dispatch({
      type: 'SET_DEFECT',
      payload: {
        data,
      },
    });
  };

  const addDefect = ({ data }: SetDefectPayloadType) => {
    dispatch({
      type: 'ADD_DEFECT',
      payload: {
        data,
      },
    });
  };

  const updateDefect = (data: SetDefectType) => {
    dispatch({
      type: 'UPDATE_DEFECT',
      payload: data,
    });
  };

  const deleteDefect = ({ id }: DeleteDefectPayloadType) => {
    dispatch({
      type: 'DELETE_DEFECT',
      payload: {
        id,
      },
    });
  };

  const deleteDefects = ({ checkpointId }: DeleteDefectsPayloadType) => {
    dispatch({
      type: 'DELETE_DEFECTS',
      payload: {
        checkpointId,
      },
    });
  };

  const updateQCPhotos = (files: UploadedFileType[], type: UploadImageType) => {
    setQCPhotos((prev) =>
      type === 'internal'
        ? { ...prev, internal: files }
        : { ...prev, public: files }
    );
  };

  const values = {
    ...state,
    setAdditionalItems,
    setAgentComment,
    setWeight,
    setHallmarks,
    agentComment,
    weight,
    hallmarks,
    additionalItems,
    setDefect,
    addDefect,
    deleteDefect,
    deleteDefects,
    agentPublicComment,
    setAgentPublicComment,
    qcPhotos,
    updateQCPhotos,
    updateDefect,
    qcData,
    setQcData,
    originCountry,
    setOriginCountry,
    isUSShipping,
    isForceSellerReturnEditable,
    additionalInfo,
    setAdditionalInfo,
    actualMeasurements,
    setActualMeasurements,
    measurementAssistantBagType,
    setMeasurementAssistantBagType,
  };

  return (
    <QualityControlContext.Provider value={values}>
      {children}
    </QualityControlContext.Provider>
  );
};

export const useQualityControlTool = () => {
  return {
    qcData: useContextSelector(QualityControlContext, (v) => v.qcData),
    setQcData: useContextSelector(QualityControlContext, (v) => v.setQcData),
    defects: useContextSelector(QualityControlContext, (v) => v.defects),
    setAdditionalItems: useContextSelector(
      QualityControlContext,
      (v) => v.setAdditionalItems
    ),
    additionalItems: useContextSelector(
      QualityControlContext,
      (v) => v.additionalItems
    ),
    agentComment: useContextSelector(
      QualityControlContext,
      (v) => v.agentComment
    ),
    weight: useContextSelector(QualityControlContext, (v) => v.weight),
    hallmarks: useContextSelector(QualityControlContext, (v) => v.hallmarks),
    setAgentComment: useContextSelector(
      QualityControlContext,
      (v) => v.setAgentComment
    ),
    setWeight: useContextSelector(QualityControlContext, (v) => v.setWeight),
    setHallmarks: useContextSelector(
      QualityControlContext,
      (v) => v.setHallmarks
    ),
    setDefect: useContextSelector(QualityControlContext, (v) => v.setDefect),
    addDefect: useContextSelector(QualityControlContext, (v) => v.addDefect),
    deleteDefect: useContextSelector(
      QualityControlContext,
      (v) => v.deleteDefect
    ),
    deleteDefects: useContextSelector(
      QualityControlContext,
      (v) => v.deleteDefects
    ),
    agentPublicComment: useContextSelector(
      QualityControlContext,
      (v) => v.agentPublicComment
    ),
    setAgentPublicComment: useContextSelector(
      QualityControlContext,
      (v) => v.setAgentPublicComment
    ),
    qcPhotos: useContextSelector(QualityControlContext, (v) => v.qcPhotos),
    updateQCPhotos: useContextSelector(
      QualityControlContext,
      (v) => v.updateQCPhotos
    ),
    updateDefect: useContextSelector(
      QualityControlContext,
      (v) => v.updateDefect
    ),
    originCountry: useContextSelector(
      QualityControlContext,
      (v) => v.originCountry
    ),
    setOriginCountry: useContextSelector(
      QualityControlContext,
      (v) => v.setOriginCountry
    ),
    isUSShipping: useContextSelector(
      QualityControlContext,
      (v) => v.isUSShipping
    ),
    isForceSellerReturnEditable: useContextSelector(
      QualityControlContext,
      (v) => v.isForceSellerReturnEditable
    ),
    additionalInfo: useContextSelector(
      QualityControlContext,
      (v) => v.additionalInfo
    ),
    setAdditionalInfo: useContextSelector(
      QualityControlContext,
      (v) => v.setAdditionalInfo
    ),
    actualMeasurements: useContextSelector(
      QualityControlContext,
      (v) => v.actualMeasurements
    ),
    setActualMeasurements: useContextSelector(
      QualityControlContext,
      (v) => v.setActualMeasurements
    ),
    measurementAssistantBagType: useContextSelector(
      QualityControlContext,
      (v) => v.measurementAssistantBagType
    ),
    setMeasurementAssistantBagType: useContextSelector(
      QualityControlContext,
      (v) => v.setMeasurementAssistantBagType
    ),
  };
};
